import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BsArrowRight,
  BsBriefcaseFill,
  BsChevronLeft,
  BsChevronRight,
  BsFileEarmarkText,
  BsFolder2Open,
  BsLinkedin,
  BsPlayCircle,
  BsRocketTakeoff
} from 'react-icons/bs';
import LazyImage from './LazyImage';
import './Home.css';

const destinationCards = [
  {
    title: 'Projects',
    text: 'Live sites, modules, demos, and code status gathered into one browsable project system.',
    link: '/projects',
    action: 'View Projects',
    icon: <BsFolder2Open />
  },
  {
    title: 'Experience',
    text: 'Resume, work history, education, certificates, and technical background in one document view.',
    link: '/experience',
    action: 'Read Resume',
    icon: <BsBriefcaseFill />
  },
  {
    title: 'Videos',
    text: 'Project walkthroughs, demos, and supporting clips for work that benefits from motion.',
    link: '/videos',
    action: 'Watch Videos',
    icon: <BsPlayCircle />
  }
];

const focusItems = ['React', 'TypeScript', 'UI systems', 'Full-stack delivery'];

const featuredLinkedInPost = {
  src: 'https://www.linkedin.com/embed/feed/update/urn:li:share:7472241217234235392?collapsed=1',
  height: 575
};

const linkedInPosts = [
  {
    src: 'https://www.linkedin.com/embed/feed/update/urn:li:share:7472642163038908417?collapsed=1',
    height: 636,
    scale: 0.94
  },
  {
    src: 'https://www.linkedin.com/embed/feed/update/urn:li:share:7400232920520613888?collapsed=1',
    height: 685,
    scale: 0.875
  },
  {
    src: 'https://www.linkedin.com/embed/feed/update/urn:li:share:7262540157814284288?collapsed=1',
    height: 685,
    scale: 0.875
  }
];

const Home = () => {
  const [activeCommunityPost, setActiveCommunityPost] = useState(0);
  const [communityPostDirection, setCommunityPostDirection] = useState(1);
  const isFirstCommunityPost = activeCommunityPost === 0;
  const isLastCommunityPost = activeCommunityPost === linkedInPosts.length - 1;

  const showPreviousCommunityPost = () => {
    if (isFirstCommunityPost) return;

    setCommunityPostDirection(-1);
    setActiveCommunityPost((current) => current - 1);
  };

  const showNextCommunityPost = () => {
    if (isLastCommunityPost) return;

    setCommunityPostDirection(1);
    setActiveCommunityPost((current) => current + 1);
  };

  return (
    <Box id="homeContainer" className="fade-in">
      <section className="home-hero">
        <Box className="home-hero-copy">
          <Typography component="p" className="home-kicker">
            Software Developer
          </Typography>
          <Typography component="h1" className="home-title">
            Dimostenis Nikolakopoulos
          </Typography>
          <Typography component="p" className="home-intro">
            React, TypeScript, and full-stack web development with a focus on practical, polished interfaces.
          </Typography>

          <Box className="home-actions">
            <Button
              component={Link}
              to="/projects"
              className="home-primary-action"
              endIcon={<BsArrowRight />}
            >
              View Projects
            </Button>
            <Button
              component={Link}
              to="/experience"
              className="home-secondary-action"
              startIcon={<BsFileEarmarkText />}
            >
              Resume
            </Button>
            <Button
              component="a"
              href="https://www.linkedin.com/in/dimos-nikolakopoulos/"
              target="_blank"
              rel="noopener noreferrer"
              className="home-secondary-action"
              startIcon={<BsLinkedin />}
            >
              LinkedIn
            </Button>
          </Box>
        </Box>

        <Box className="home-portrait-panel">
          <LazyImage src="/assets/meMostRectent.png" alt="Dimostenis Nikolakopoulos" className="home-portrait" />
          <Box className="home-portrait-caption">
            <LazyImage src="/assets/accentureLogo.png" alt="Accenture" className="home-workplace-logo" />
            <Box>
              <Typography component="span" className="home-workplace-name">Accenture</Typography>
              <Typography component="span" className="home-workplace-role">Software Engineer</Typography>
            </Box>
          </Box>
        </Box>
      </section>

      <section className="home-focus-strip" aria-label="Current focus">
        <Typography component="span" className="home-focus-label">
          Current focus
        </Typography>
        <Box className="home-focus-items">
          {focusItems.map((item) => (
            <Typography key={item} component="span">
              {item}
            </Typography>
          ))}
        </Box>
      </section>

      <section className="home-lower-content">
        <Box
          className="home-linkedin-feature"
          aria-label="Featured LinkedIn posts"
        >
          <Typography component="h2" className="home-linkedin-title">
            <BsLinkedin />
            Community Posts
          </Typography>
          <Box
            className="home-linkedin-scroll-shell"
          >
            {linkedInPosts.map((post, index) => {
              const active = index === activeCommunityPost;

              return (
                <motion.div
                  key={post.src}
                  className="home-community-post-frame"
                  aria-hidden={!active}
                  style={{
                    '--community-post-height': `${post.height}px`,
                    '--community-post-scale': post.scale
                  }}
                  initial={false}
                  animate={{
                    opacity: active ? 1 : 0,
                    x: active ? 0 : communityPostDirection * -34,
                    scale: active ? 1 : 0.985,
                    zIndex: active ? 2 : 1
                  }}
                  transition={{ duration: 0.32, ease: 'easeOut' }}
                >
                  <iframe
                    src={post.src}
                    frameBorder="0"
                    loading="eager"
                    scrolling="no"
                    allowFullScreen
                    title={`Embedded LinkedIn post ${index + 1}`}
                  />
                </motion.div>
              );
            })}
          </Box>
          <Box className="home-linkedin-pagination" aria-label="Community post pagination">
            <button
              type="button"
              onClick={showPreviousCommunityPost}
              aria-label="Previous community post"
              disabled={isFirstCommunityPost}
            >
              <BsChevronLeft />
            </button>
            <Typography component="span">
              {activeCommunityPost + 1} / {linkedInPosts.length}
            </Typography>
            <button
              type="button"
              onClick={showNextCommunityPost}
              aria-label="Next community post"
              disabled={isLastCommunityPost}
            >
              <BsChevronRight />
            </button>
          </Box>
        </Box>

        <Box className="home-featured-linkedin-post">
          <Typography component="h2" className="home-featured-linkedin-title">
            <BsRocketTakeoff />
            Most Recent Independent Work
          </Typography>
          <iframe
            src={featuredLinkedInPost.src}
            height={featuredLinkedInPost.height}
            width="504"
            frameBorder="0"
            loading="lazy"
            allowFullScreen
            title="Embedded LinkedIn post"
          />
          <Button
            component="a"
            href="https://cataphractvisuals.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="home-featured-linkedin-action"
            endIcon={<BsArrowRight />}
          >
            Visit Cataphract Visuals
          </Button>
        </Box>

        <Box className="home-destinations" aria-label="Portfolio sections">
          {destinationCards.map((card) => (
            <Box key={card.title} className="home-destination-card">
              <Box className="home-destination-icon">{card.icon}</Box>
              <Typography component="h2">{card.title}</Typography>
              <Typography component="p">{card.text}</Typography>
              <Button component={Link} to={card.link} className="home-card-action" endIcon={<BsArrowRight />}>
                {card.action}
              </Button>
            </Box>
          ))}
        </Box>
      </section>
    </Box>
  );
};

export default Home;
